export default defineNuxtPlugin(async (nuxtApp) => {
  const { fetchSettings, themeCss, themeSwitchEnabled, branding, site, loaded, resetForShopChange } = useSiteSettings()
  const shopSlug = useShopSlug()
  const { customDomainMode } = useShopDomain()

  if (import.meta.server) {
    await fetchSettings()
  }

  nuxtApp.hook('app:mounted', () => {
    void fetchSettings()
  })

  if (import.meta.client) {
    watch(shopSlug, (next, prev) => {
      if (next === prev) return
      resetForShopChange()
      void fetchSettings()
    })
  }

  useHead({
    titleTemplate: (titleChunk?: string) => {
      const name = site.value.name
      return titleChunk && titleChunk !== name
        ? `${titleChunk} | ${name}`
        : name
    },
    meta: [
      {
        name: 'theme-color',
        content: computed(() => branding.value.themeColor),
      },
      {
        property: 'og:site_name',
        content: computed(() => site.value.name),
      },
      {
        name: 'mobile-web-app-capable',
        content: 'yes',
      },
      {
        name: 'apple-mobile-web-app-capable',
        content: 'yes',
      },
      {
        name: 'apple-mobile-web-app-title',
        content: computed(() => site.value.name),
      },
      {
        name: 'apple-mobile-web-app-status-bar-style',
        content: 'default',
      },
    ],
    link: computed(() => {
      const favicon: Record<string, string> = {
        key: 'site-favicon',
        rel: 'icon',
        href: branding.value.favicon,
      }
      if (branding.value.faviconType) {
        favicon.type = branding.value.faviconType
      }
      const slug = shopSlug.value
      const manifestHref = customDomainMode.value || !slug
        ? '/manifest.webmanifest'
        : `/${slug}/manifest.webmanifest`
      return [
        favicon,
        {
          key: 'site-apple-touch-icon',
          rel: 'apple-touch-icon',
          sizes: '180x180',
          href: branding.value.appleTouchIcon,
        },
        {
          key: 'site-manifest',
          rel: 'manifest',
          href: manifestHref,
        },
      ]
    }),
    style: computed(() => [
      {
        id: 'site-theme-vars',
        innerHTML: themeCss.value,
      },
    ]),
  })

  const colorMode = useColorMode()
  const route = useRoute()

  function isAdminOrPlatformPath(path: string) {
    return path.startsWith('/platform') || /\/admin(\/|$)/.test(path)
  }

  function applyForcedLight() {
    colorMode.preference = 'light'
    colorMode.value = 'light'
    colorMode.forced = true
    if (import.meta.client) {
      try {
        localStorage.setItem('nuxt-color-mode', 'light')
      } catch {
        // ignore quota / private mode
      }
    }
  }

  watch(
    [loaded, themeSwitchEnabled, shopSlug],
    ([isLoaded, enabled]) => {
      if (!isLoaded) return

      if (!enabled) {
        route.meta.colorMode = 'light'
        applyForcedLight()
        return
      }

      // Theme switch back on: stop forcing storefront (admin/platform stay light via middleware)
      if (!isAdminOrPlatformPath(route.path) && route.meta.colorMode === 'light') {
        delete route.meta.colorMode
        colorMode.forced = false
        colorMode.value = colorMode.preference === 'system'
          ? (import.meta.client && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
          : colorMode.preference
      }
    },
    { immediate: true },
  )
})
